import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type {
  ClientMessage,
  ServerMessage,
} from "@binogi-livequiz/shared";
import { getConfig } from "./config";

export type WsStatus = "idle" | "connecting" | "open" | "closed";

interface WsContextValue {
  status: WsStatus;
  lastMessage: ServerMessage | null;
  connect: () => void;
  disconnect: () => void;
  send: (msg: ClientMessage) => void;
  /** Resets `lastMessage` to null — useful after a route handles a message. */
  consume: () => void;
}

const WsContext = createContext<WsContextValue | null>(null);

/** Messages that establish this socket's identity with the server. */
const isJoin = (m: ClientMessage) =>
  m.type === "JOIN_SESSION" ||
  m.type === "HOST_ATTACH";

/**
 * The only frames that answer a join and therefore prove this socket is
 * joined. The first *inbound* frame is NOT a safe proxy for this: the
 * backend writes the PLAYER row before the CONNECTIONS row, so a broadcast
 * (PLAYER_JOINED, QUESTION_START, PLAYER_LEFT…) triggered by our own join
 * can reach us ahead of our SESSION_JOINED. Releasing on that would drain a
 * held SUBMIT_ANSWER onto a socket the server does not yet consider joined —
 * "Not joined to a session", the exact failure the hold exists to prevent.
 */
const isJoinAck = (m: ServerMessage) =>
  m.type === "SESSION_JOINED" ||
  m.type === "ERROR";

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<WsStatus>("idle");
  const [lastMessage, setLastMessage] = useState<ServerMessage | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const queueRef = useRef<ClientMessage[]>([]);
  const reconnectGuardRef = useRef(false);
  // Join-ack gating. Ordering the frames on the wire is NOT enough:
  // API Gateway invokes a separate concurrent $default Lambda per frame,
  // so a JOIN_SESSION and a SUBMIT_ANSWER written ~0 ms apart can still be
  // processed submit-first — the student gets "Not joined to a session"
  // and the answer is lost. So once a join goes out on a socket we hold
  // everything else until the server's first frame back on that socket
  // (which is always the join's response: SESSION_JOINED / ERROR), or a
  // 3 s fallback if it never arrives.
  const holdRef = useRef(false);
  const heldRef = useRef<ClientMessage[]>([]);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const rawSend = useCallback((ws: WebSocket, m: ClientMessage): boolean => {
    try {
      ws.send(JSON.stringify(m));
      return true;
    } catch {
      return false;
    }
  }, []);

  /** Release the hold and send everything that piled up behind the join. */
  const releaseHold = useCallback(() => {
    if (holdTimerRef.current !== null) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (!holdRef.current) return;
    holdRef.current = false;
    const held = heldRef.current;
    heldRef.current = [];
    const ws = wsRef.current;
    for (const m of held) {
      if (ws && ws.readyState === WebSocket.OPEN && rawSend(ws, m)) continue;
      queueRef.current.push(m);
    }
  }, [rawSend]);

  const flushQueue = useCallback(
    (ws: WebSocket) => {
      // A reconnect can queue SUBMIT_ANSWER before the route's rejoin effect
      // queues JOIN_SESSION. The server rejects anything sent on a socket
      // that hasn't joined, so joins/attaches always go out first.
      const queued = queueRef.current;
      queueRef.current = [];
      const joins = queued.filter(isJoin);
      const rest = queued.filter((m) => !isJoin(m));

      for (const m of joins) {
        if (!rawSend(ws, m)) queueRef.current.push(m);
      }

      if (joins.length === 0) {
        // Nothing to wait on — behave exactly as before.
        for (const m of rest) {
          if (!rawSend(ws, m)) queueRef.current.push(m);
        }
        return;
      }

      // Hold the rest until the join is acked (see holdRef above).
      holdRef.current = true;
      heldRef.current = [...heldRef.current, ...rest];
      if (holdTimerRef.current !== null) clearTimeout(holdTimerRef.current);
      holdTimerRef.current = setTimeout(() => {
        holdTimerRef.current = null;
        releaseHold();
      }, 3000);
    },
    [rawSend, releaseHold],
  );

  const connect = useCallback(() => {
    if (wsRef.current) {
      const rs = wsRef.current.readyState;
      if (rs === WebSocket.CONNECTING || rs === WebSocket.OPEN) return;
    }
    const wsUrl = getConfig().wsUrl;
    if (!wsUrl) {
      console.error(
        "WebSocket URL is empty. Did /config.json fail to load, and is VITE_WS_URL set for local dev?",
      );
      return;
    }
    setStatus("connecting");
    console.warn("[ws-context] opening new WebSocket", {
      stack: new Error().stack?.split("\n").slice(1, 5),
    });
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onopen = () => {
      setStatus("open");
      flushQueue(ws);
    };
    ws.onmessage = (event) => {
      let parsed: ServerMessage;
      try {
        parsed = JSON.parse(event.data) as ServerMessage;
      } catch {
        // ignore malformed frame
        return;
      }
      // Only the join's own response clears the hold (see isJoinAck) —
      // a broadcast that overtakes it must not. The 3 s fallback timer in
      // flushQueue is the safety valve if no ack ever arrives. Release
      // before surfacing the message so anything a route sends in response
      // goes out behind the held batch, in order. An ERROR means the join
      // was refused and this socket is not joined, so the held batch could
      // only come back "Not attached to a session" and bury the refusal
      // (a replaced host screen would never learn it was replaced): drop it.
      if (wsRef.current === ws && isJoinAck(parsed)) {
        if (parsed.type === "ERROR" && holdRef.current) heldRef.current = [];
        releaseHold();
      }
      setLastMessage(parsed);
    };
    ws.onerror = () => {
      // Browsers fire onclose right after onerror in most cases.
    };
    ws.onclose = () => {
      if (wsRef.current === ws) {
        wsRef.current = null;
        setStatus("closed");
        // The ack we were holding for will never arrive on a dead socket.
        // Requeue the held messages so the next connect's flush re-orders
        // them behind a fresh join.
        if (holdTimerRef.current !== null) {
          clearTimeout(holdTimerRef.current);
          holdTimerRef.current = null;
        }
        holdRef.current = false;
        queueRef.current.push(...heldRef.current);
        heldRef.current = [];
      }
    };
  }, [flushQueue, releaseHold]);

  const disconnect = useCallback(() => {
    const ws = wsRef.current;
    wsRef.current = null;
    queueRef.current = [];
    heldRef.current = [];
    holdRef.current = false;
    if (holdTimerRef.current !== null) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (ws) {
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    }
    setStatus("idle");
  }, []);

  const send = useCallback(
    (msg: ClientMessage) => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        // A join still waiting for its ack holds back everything else —
        // otherwise a tap landing 5 ms after the rejoin races the join's
        // own Lambda invocation and comes back "Not joined to a session".
        if (holdRef.current && !isJoin(msg)) {
          heldRef.current.push(msg);
          return;
        }
        ws.send(JSON.stringify(msg));
        return;
      }
      queueRef.current.push(msg);
      connect();
    },
    [connect],
  );

  const consume = useCallback(() => setLastMessage(null), []);

  useEffect(() => {
    return () => {
      // Guard against React 18 StrictMode double-effect tearing down the
      // socket immediately after it opens during development.
      if (reconnectGuardRef.current) return;
      reconnectGuardRef.current = true;
      queueMicrotask(() => {
        reconnectGuardRef.current = false;
      });
      // We intentionally do NOT close the socket on unmount of the provider —
      // the provider lives at the app root and unmount only happens at full
      // page navigation, at which point the browser closes the socket anyway.
    };
  }, []);

  const value = useMemo<WsContextValue>(
    () => ({ status, lastMessage, connect, disconnect, send, consume }),
    [status, lastMessage, connect, disconnect, send, consume],
  );

  return <WsContext.Provider value={value}>{children}</WsContext.Provider>;
}

export function useWebSocket(): WsContextValue {
  const ctx = useContext(WsContext);
  if (!ctx) {
    throw new Error(
      "useWebSocket must be used inside <WebSocketProvider>",
    );
  }
  return ctx;
}
